import { SELECTABLE_BOT_CATALOG, type DiceLuckHistory } from '@warcarp/api-types'
import { formatDiceLuckVerdict } from './diceLuck'

export interface BotLeaderboardEntry {
  rank: number
  botProfile: string
  botUserId: string
  displayName: string
  strategyVersion: number
  gamesPlayed: number
  wins: number
  firstPlaceRate: number
  podiums: number
  podiumRate: number
  averagePlacement: number
  turnCapGames: number
  turnCapRate: number
}

export interface BotStatsResponse {
  entries: BotLeaderboardEntry[]
  /** All-time snapshot-backed luck, independent of the current leaderboard season. */
  diceLuck?: Record<string, DiceLuckHistory>
}

export function botProfileRecordStats(stats: BotLeaderboardEntry | undefined) {
  return [
    { label: 'Games played', value: stats ? String(stats.gamesPlayed) : '—' },
    { label: 'Wins', value: stats ? String(stats.wins) : '—' },
    {
      label: 'Podiums',
      value: stats ? `${stats.podiums} (${formatBotPct(stats.podiumRate)})` : '—',
    },
    {
      label: 'Average finish',
      value: stats ? stats.averagePlacement.toFixed(1) : '—',
    },
  ]
}

/** Gallery standings only; direct profiles still use the complete identity catalog. */
export function mergeBotStats(
  entries: BotLeaderboardEntry[],
  diceLuck: Record<string, DiceLuckHistory> = {},
) {
  const statsByProfile = new Map(entries.map((entry) => [entry.botProfile, entry]))

  // Ranked results lead the gallery. Bots without completed games retain their
  // stable catalog order, which is also the seeded roster order (Hannibal
  // first) and gives a deterministic fresh-start ranking.
  return SELECTABLE_BOT_CATALOG
    .map((bot, catalogIndex) => ({
      ...bot,
      catalogIndex,
      stats: statsByProfile.get(bot.key) ?? null,
      diceLuck: diceLuck[bot.key] ?? null,
    }))
    .sort((a, b) => {
      const aRank = a.stats?.rank ?? Number.POSITIVE_INFINITY
      const bRank = b.stats?.rank ?? Number.POSITIVE_INFINITY
      return aRank - bRank || a.catalogIndex - b.catalogIndex
    })
    .map(({ catalogIndex: _catalogIndex, ...bot }, index) => ({
      ...bot,
      // Rank within the visible roster, without gaps from retired identities.
      // Keep the original record/rank untouched in stats for historical uses.
      displayRank: index + 1,
    }))
}

export function formatBotCardStats(
  stats: BotLeaderboardEntry | null,
  diceLuck: DiceLuckHistory | null,
): string {
  const luck = diceLuck ? formatDiceLuckVerdict(diceLuck.verdict) : 'No luck history'
  const rolls = diceLuck ? `${diceLuck.rollCount} rolls` : '0 recorded rolls'
  const record = stats
    ? `${stats.wins} ${stats.wins === 1 ? 'win' : 'wins'} · ${stats.averagePlacement.toFixed(1)} avg finish · ${stats.gamesPlayed} ${stats.gamesPlayed === 1 ? 'game' : 'games'}`
    : 'No completed games'
  return `${record}\nDice luck: ${luck} · ${rolls}`
}

export function formatBotPct(value: number) {
  if (value <= 0) return '0%'
  return `${Math.round(value * 1000) / 10}%`
}
